/**
 * Fullscreen helper.
 * Главное правило: requestFullscreen ОБЯЗАН вызываться синхронно в том же
 * tick'е, что и user-gesture (pointerdown / keydown). Иначе мобильный
 * браузер тихо отказывает.
 *
 * Реализация: используем Phaser ScaleManager (он сам префиксы разруливает).
 * Если браузер не поддерживает fullscreen API (типичный случай — iOS
 * Safari), включаем iOS-immersive: CSS-класс на body, скролл хака для
 * скрытия address-bar, попытка orientation lock.
 */

import Phaser from "phaser";

export function isNativeFsSupported(): boolean {
  const d: any = document.documentElement;
  return !!(d.requestFullscreen || d.webkitRequestFullscreen);
}

function isIOS(): boolean {
  // iPad на iPadOS 13+ выдаёт MacIntel + touch; учитываем оба
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) ||
         (ua.includes("Mac") && (navigator.maxTouchPoints || 0) > 1);
}

const IOS_CLASS = "banzai-ios-immersive";
let iosImmersive = false;

function ensureIosStyles() {
  if (document.getElementById("banzai-ios-fs-style")) return;
  const style = document.createElement("style");
  style.id = "banzai-ios-fs-style";
  style.textContent = `
    body.${IOS_CLASS} { overflow: hidden; position: fixed; inset: 0; }
    body.${IOS_CLASS} #game {
      width: 100vw !important;
      height: 100vh !important;
      height: 100dvh !important;
    }
    body.${IOS_CLASS} canvas {
      max-width: 100vw !important;
      max-height: 100vh !important;
      max-height: 100dvh !important;
    }
  `;
  document.head.appendChild(style);
}

function enterIosImmersive() {
  ensureIosStyles();
  document.body.classList.add(IOS_CLASS);
  iosImmersive = true;
  // прокручиваем чуть вниз — мобильный Safari прячет адресную строку
  setTimeout(() => window.scrollTo(0, 1), 50);
  tryLockLandscape();
  notifyChange();
}

function exitIosImmersive() {
  document.body.classList.remove(IOS_CLASS);
  iosImmersive = false;
  notifyChange();
}

function tryLockLandscape() {
  const so: any = (screen as any).orientation;
  if (so && typeof so.lock === "function") {
    try { so.lock("landscape").catch(() => {}); } catch (_e) {}
  }
}

export function isFullscreen(): boolean {
  const d: any = document;
  return !!(d.fullscreenElement || d.webkitFullscreenElement) || iosImmersive;
}

/**
 * Синхронный запрос fullscreen — вызывать ТОЛЬКО из event handler'а
 * (pointerdown/keydown/click). Никаких await перед этим вызовом.
 */
export function startFullscreenSync(scale?: Phaser.Scale.ScaleManager): void {
  if (isNativeFsSupported()) {
    if (scale && !scale.isFullscreen) {
      try { scale.startFullscreen({ navigationUI: "hide" } as any); } catch (_e) {}
    } else {
      const el: any = document.documentElement;
      try {
        if (el.requestFullscreen) el.requestFullscreen({ navigationUI: "hide" } as any).catch(() => {});
        else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
      } catch (_e) {}
    }
    tryLockLandscape();
  } else if (isIOS()) {
    enterIosImmersive();
  }
}

export function stopFullscreenSync(scale?: Phaser.Scale.ScaleManager): void {
  if (iosImmersive) { exitIosImmersive(); return; }
  if (scale && scale.isFullscreen) { try { scale.stopFullscreen(); } catch (_e) {} return; }
  const d: any = document;
  try {
    if (d.exitFullscreen) d.exitFullscreen().catch(() => {});
    else if (d.webkitExitFullscreen) d.webkitExitFullscreen();
  } catch (_e) {}
}

export function toggleFullscreenSync(scale?: Phaser.Scale.ScaleManager): void {
  if (isFullscreen()) stopFullscreenSync(scale);
  else startFullscreenSync(scale);
}

// ── change-event hub для UI ────────────────────────────────────────────────
const changeListeners = new Set<() => void>();
function notifyChange() { for (const l of changeListeners) try { l(); } catch (_e) {} }
document.addEventListener("fullscreenchange", notifyChange);
document.addEventListener("webkitfullscreenchange", notifyChange);

/**
 * UI-кнопка в углу сцены. Тап = синхронный toggle.
 */
export function addFullscreenButton(scene: Phaser.Scene, x?: number, y?: number) {
  const W = scene.scale.gameSize.width;
  const px = x ?? W - 40;
  const py = y ?? 25;

  const btn = scene.add.text(px, py, "⛶", {
    fontFamily: "Arial Black", fontSize: "26px",
    color: "#ffffff", backgroundColor: "#000000",
    padding: { x: 10, y: 6 } as any,
    stroke: "#888888", strokeThickness: 2,
  } as any).setOrigin(0.5).setDepth(2000).setInteractive({ useHandCursor: true });

  const refresh = () => btn.setText(isFullscreen() ? "⛶✓" : "⛶");

  // КРИТИЧНО: вызываем toggle синхронно прямо в pointerdown,
  // без promise/setTimeout/await — иначе мобильный браузер блокирует.
  btn.on("pointerdown", () => {
    toggleFullscreenSync(scene.scale);
    // обновим иконку чуть позже — fullscreenchange прилетит асинхронно
    scene.time.delayedCall(150, refresh);
  });

  changeListeners.add(refresh);
  scene.events.once("shutdown", () => {
    changeListeners.delete(refresh);
    btn.destroy();
  });

  return btn;
}

/**
 * Авто-fullscreen при первом тапе на тач-устройствах.
 * Используется один раз за загрузку.
 */
let autoTriggered = false;
export function armAutoFullscreenOnFirstTap() {
  if (autoTriggered) return;
  const isTouch = ("ontouchstart" in window) || (navigator.maxTouchPoints || 0) > 0;
  if (!isTouch) return;

  const handler = () => {
    if (autoTriggered) return;
    autoTriggered = true;
    // синхронно из user-gesture
    startFullscreenSync();
    window.removeEventListener("pointerdown", handler, true);
    window.removeEventListener("touchend", handler, true);
  };
  // capture phase — чтобы поймать раньше Phaser handler'ов
  window.addEventListener("pointerdown", handler, { capture: true });
  window.addEventListener("touchend", handler, { capture: true });
}
